import React, { useState } from 'react';
import styled from 'styled-components';
import { Grid, Type } from '../../../app/StyleGuide';
import { PrimaryButton } from '../../../components/Buttons';
import ErrorMessage from '../../../components/ErrorMessage';
import alertService from '../../../services/AlertService';
import { logger } from '../../../utils/logger';
import { completeAssessmentResponse } from '../api/completeAssessmentResponse';
import { invalidateAssessmentCache } from '../api/getAssessmentById';
import { saveLikertAnswer } from '../api/saveLikertAnswer';
import { AssessmentState, LikertQuestion as LikertQuestionType } from '../models/assessment';
import { isQuestionAnswered } from '../utils/isQuestionAnswered';
import { pollForCompletedAssessment } from '../utils/pollForCompletedAssessment';
import LikertQuestion from './LikertQuestion';

const QuestionContainer = styled.div`
  margin: ${Grid._5} auto;
  .question-label {
    font-size: ${Type.Scale._3};
    color: ${Type.Color.medium};
    padding: 0;
  }
`;

type AssessmentTakerProps = {
  assessment: AssessmentState;
  responseId: string;
  onAllQuestionsAnswered: (completedAssessment: AssessmentState) => void;
};

export function AssessmentTaker({ assessment, responseId, onAllQuestionsAnswered }: AssessmentTakerProps) {
  const { questions, likertScales } = assessment;
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(() => {
    const idx = questions.findIndex(q => !isQuestionAnswered(q));
    return idx >= 0 ? idx : 0;
  });
  const [currentQuestionAnswer, setCurrentQuestionAnswer] = useState<number | null>(null);
  const [isNotAnsweredError, setIsNotAnsweredError] = useState(false);
  const [isFinishing, setIsFinishing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  if (!responseId) {
    logger.error('AssessmentTaker rendered without a responseId');
    return <ErrorMessage />;
  }

  const currentQuestion = questions[currentQuestionIndex];
  const isLastQuestion = currentQuestionIndex + 1 >= questions.length;

  const saveAndAdvance = () => {
    if (currentQuestionAnswer === null) {
      setIsNotAnsweredError(true);
      return;
    }

    setIsSaving(true);
    setIsNotAnsweredError(false);

    saveLikertAnswer({
      assessmentResponseID: responseId,
      questionID: currentQuestion.questionID,
      selectedScore: currentQuestionAnswer,
      assessmentEventSequenceNo: assessment.eventSequenceNo,
    })
      .then(() => {
        invalidateAssessmentCache(assessment.assessmentID);

        if (!isLastQuestion) {
          setCurrentQuestionAnswer(null);
          setCurrentQuestionIndex(prev => prev + 1);
          window.scrollTo(0, 0);
          return;
        }

        setIsFinishing(true);
        return completeAssessmentResponse({
          assessmentResponseID: responseId,
          assessmentEventSequenceNo: assessment.eventSequenceNo,
        })
          .then(() => pollForCompletedAssessment(assessment.assessmentID, responseId))
          .then(completedAssessment => {
            onAllQuestionsAnswered(completedAssessment);
          });
      })
      .catch(error => {
        logger.error('Failed to save answer:', error);
        alertService.show('Failed to save answer. Please try again.', 'error');
      })
      .finally(() => {
        setIsSaving(false);
        setIsFinishing(false);
      });
  };

  const likertScale =
    currentQuestion.kind === 'Likert' ? likertScales[(currentQuestion as LikertQuestionType).likertScaleID] : undefined;

  return (
    <QuestionContainer>
      <p className="question-label">
        Question {currentQuestionIndex + 1} of {questions.length}
      </p>
      <p className="question-text">{currentQuestion.text}</p>
      {currentQuestion.kind === 'Likert' && likertScale && (
        <LikertQuestion
          key={currentQuestion.questionID}
          likertScale={likertScale}
          setCurrentQuestionAnswer={setCurrentQuestionAnswer}
        />
      )}
      {isNotAnsweredError && <ErrorMessage>An answer is required</ErrorMessage>}
      <p>
        <PrimaryButton
          data-qa-hook="nextStepInAssessmentButton"
          disabled={isSaving || isFinishing}
          operating={isSaving || isFinishing}
          style={{ float: 'right' }}
          onClick={saveAndAdvance}
        >
          {isLastQuestion ? 'Finish' : 'Next'}
        </PrimaryButton>
      </p>
    </QuestionContainer>
  );
}
